import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode
} from 'react'
import useSWR from 'swr'
import { fetchAvailableCountries, updateCartBuyerIdentity } from '@api/graphqlShopify'
import { ShopifyAvailableCountry } from '@api/graphqlShopify/types'
import { getCookieByName, setCookie } from '@helpers/session'
import { getNativeCurrencyCode } from '@helpers/env'
import {
  getSelectedCountry,
  getSelectedCurrency
} from '@helpers/currency'
import {
  COOKIE_EXPIRE_1_YEAR,
  CURRENCY_SELECTION_ENABLED,
  GEO_COUNTRY_CODE_COOKIE,
  SELECTED_COUNTRY_COOKIE,
  SELECTED_CURRENCY_COOKIE
} from '@consts/index'
import trackError from '@helpers/trackError'

type CurrencyContextValue = {
  supportedCurrencies: SupportedCurrency[]
  selectedCurrency: string
  setSelectedCurrency: (code: string) => void
  selectedCountry: string
  isLoading: boolean
}

type SupportedCurrency = {
  code: string
  symbol: string
  name: string
}

const CurrencyContext = createContext<CurrencyContextValue>({
  supportedCurrencies: [],
  selectedCurrency: '',
  setSelectedCurrency: () => undefined,
  selectedCountry: '',
  isLoading: false
})

export function useCurrency(): CurrencyContextValue {
  return useContext(CurrencyContext)
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [selectedCurrency, setSelectedCurrency] = useState<string>(CURRENCY_SELECTION_ENABLED ? getNativeCurrencyCode() : '')
  const [selectedCountry, setSelectedCountry] = useState<string>('')

  const { data: availableCountries, isLoading } = useSWR(
    CURRENCY_SELECTION_ENABLED ? 'available-countries' : null,
    fetchAvailableCountries,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: error => {
        trackError(new Error('Failed to fetch store localization'), { error: error as Error, section: 'Currency' })
      }
    }
  )

  useEffect(() => {
    if (!availableCountries) return

    initSelectedCurrency(availableCountries)
    initSelectedCountry(availableCountries)

    setSelectedCurrency(getSelectedCurrency() || getNativeCurrencyCode())
    setSelectedCountry(getSelectedCountry() || '')
  }, [availableCountries])

  const supportedCurrencies = availableCountries ? dedupeCurrencies(availableCountries) : []

  return (
    <CurrencyContext.Provider value={{
      supportedCurrencies,
      selectedCurrency,
      setSelectedCurrency: selectCurrency,
      selectedCountry,
      isLoading
    }}>
    {children}
    </CurrencyContext.Provider>
  )

  async function selectCurrency(code: string): Promise<void> {
    if (code === (getSelectedCurrency() || getNativeCurrencyCode())) return

    const country = getCountryForCurrency(code, availableCountries || []) || ''
    setCookie(SELECTED_CURRENCY_COOKIE, code, COOKIE_EXPIRE_1_YEAR)
    setCookie(SELECTED_COUNTRY_COOKIE, country, COOKIE_EXPIRE_1_YEAR)

    await updateCartBuyerIdentity(country || undefined).catch(() => {})

    window.location.reload()
  }
}

function dedupeCurrencies(countries: ShopifyAvailableCountry[]): SupportedCurrency[] {
  const currencies = new Map<string, SupportedCurrency>()

  countries.forEach(({ currency }) => {
    if (currency && !currencies.has(currency.isoCode)) {
      currencies.set(currency.isoCode, {
        code: currency.isoCode,
        symbol: currency.symbol,
        name: currency.name
      })
    }
  })

  return Array.from(currencies.values())
}

function initSelectedCurrency(availableCountries: ShopifyAvailableCountry[]): void {
  if (getSelectedCurrency()) return
  const country = getCookieByName(GEO_COUNTRY_CODE_COOKIE)?.trim().toUpperCase()
  const currency = getCurrencyForCountry(country, availableCountries) || getNativeCurrencyCode()
  setCookie(SELECTED_CURRENCY_COOKIE, currency, COOKIE_EXPIRE_1_YEAR)
}

function getCurrencyForCountry(
  countryCode: string | undefined,
  availableCountries: ShopifyAvailableCountry[]
): string | undefined {
  if (!countryCode) return undefined

  return availableCountries.find(
    country => country.isoCode.toUpperCase() === countryCode.toUpperCase()
  )?.currency.isoCode
}

function initSelectedCountry(availableCountries: ShopifyAvailableCountry[]): void {
  const currency = getSelectedCurrency() || getNativeCurrencyCode()
  const country = getCountryForCurrency(currency, availableCountries) || ''
  setCookie(SELECTED_COUNTRY_COOKIE, country, COOKIE_EXPIRE_1_YEAR)
}

function getCountryForCurrency(currency: string, availableCountries: ShopifyAvailableCountry[]): string | undefined {
  return availableCountries.find(country => country.currency.isoCode.toUpperCase() === currency.toUpperCase())?.isoCode
}
